/**
 * LinkItemDialog — search dialog for manually linking an item to another.
 *
 * Two-step flow: search → confirm. Server-side filtering with 300ms debounce.
 * Uses existing Dialog + Command components.
 *
 * Plan 191, Epic 03.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { ChannelLogo } from "./ChannelLogo";
import { useSearchLinkableItems, useLinkItem } from "../api/listingsHooks";
import type { LinkableItem } from "../api/listingsApi";

interface LinkItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The item we're linking FROM (will be archived) */
  sourceItemCode: string;
  /** Called after successful link with the target item code */
  onLinked?: (targetItemCode: string) => void;
}

export function LinkItemDialog({
  open,
  onOpenChange,
  sourceItemCode,
  onLinked,
}: LinkItemDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<LinkableItem | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const { data: results = [], isLoading } = useSearchLinkableItems(
    debouncedSearch,
    sourceItemCode,
  );
  const linkMutation = useLinkItem();

  const handleSelect = (item: LinkableItem) => {
    setSelectedItem(item);
  };

  const handleLink = () => {
    if (!selectedItem) return;
    linkMutation.mutate(
      { sourceItemCode, targetItemCode: selectedItem.item_code },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          resetState();
          onLinked?.(data.target_item_code);
        },
      },
    );
  };

  const resetState = () => {
    setSearch("");
    setSelectedItem(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link to existing item</DialogTitle>
        </DialogHeader>

        {/* Step 1: Search */}
        {!selectedItem && (
          <Command shouldFilter={false} className="rounded-lg border">
            <CommandInput
              placeholder="Search by name, SKU, or item code"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[300px]">
              {debouncedSearch.length >= 2 && !isLoading && results.length === 0 && (
                <CommandEmpty>No matching items found</CommandEmpty>
              )}
              {results.map((item) => (
                <CommandItem
                  key={item.item_code}
                  value={item.item_code}
                  onSelect={() => handleSelect(item)}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      className="h-10 w-10 rounded-md object-cover bg-muted shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-muted shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {item.item_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item.item_code}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {item.channels.map((ch) => (
                      <ChannelLogo key={ch} channel={ch} size={14} />
                    ))}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        )}

        {/* Step 2: Confirm */}
        {selectedItem && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border p-4">
              {selectedItem.image ? (
                <img
                  src={selectedItem.image}
                  alt=""
                  className="h-12 w-12 rounded-md object-cover bg-muted shrink-0"
                />
              ) : (
                <div className="h-12 w-12 rounded-md bg-muted shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {selectedItem.item_name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {selectedItem.item_code}
                </div>
                <div className="flex gap-1 mt-1">
                  {selectedItem.channels.map((ch) => (
                    <ChannelLogo key={ch} channel={ch} size={12} />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              All marketplace listings will be moved to this item. The current
              item will be archived.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setSelectedItem(null)}
              >
                Back
              </Button>
              <Button
                onClick={handleLink}
                disabled={linkMutation.isPending}
              >
                {linkMutation.isPending ? "Linking…" : "Link"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
